import { Injectable } from "@angular/core";
import { Geolocation } from "@capacitor/geolocation";
import { GoogleMap, LatLngBounds } from "@capacitor/google-maps";
import { partition } from "lodash-es";
import { Directory } from "@capacitor/filesystem";
import { DomSanitizer } from "@angular/platform-browser";
import { AuthService } from "./auth.service";
import { MapEvents } from "../types/Map/MapEvents";
import { MapMarker } from "../types/Map/MapMarker";
import { SearchLocation } from "../types/Location/SearchLocation";
import { Location } from "../types/Location/Location";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { FileService } from "./file.service";
import { RequestService } from "./request.service";
import { LocationListItem } from "../types/Location/LocationListItem";
import { Locations } from "../types/Location/Locations";

@Injectable({
  providedIn: "root",
})
export class MapService extends BaseService {
  isSyncOpen: boolean = false;

  errorDuration: number = 5000;

  isFiltersOpen: boolean = false;

  constructor(
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private requestService: RequestService,
    private fileService: FileService,
    logService: LogService,
  ) {
    super(logService, "MapService");
  }

  async createMap(options: any) {
    if (!options.element) {
      this.logger.error("No element provided to create map");
      return false;
    }
    const geolocation = await Geolocation.getCurrentPosition();
    const { mapKey: apiKey } = await this.authService.getAuth();
    const map = await GoogleMap.create({
      id: "app-google-map",
      forceCreate: true,
      element: options.element,
      apiKey,
      config: {
        streetViewControlOptions: {
          position: 6,
        },
        zoomControlOptions: {
          position: 6,
        },
        center: {
          lat: options?.center?.lat || geolocation?.coords?.latitude,
          lng: options?.center?.lng || geolocation?.coords?.longitude,
        },
        mapTypeControl: false,
        fullscreenControl: false,
        zoom: options.zoom || 8,
        zoomControl: true,
        streetViewControl: true,
      } as any,
    });
    await map.enableCurrentLocation(true);
    await this.setMapEvents(map, options.events);
    return map;
  }

  public async resetCameraToDefaultLocation(map: GoogleMap, zoom: number = 8) {
    const geolocation = await Geolocation.getCurrentPosition();
    await map.setCamera({
      coordinate: {
        lat: geolocation?.coords?.latitude,
        lng: geolocation?.coords?.longitude,
      },
      zoom,
    });
  }

  public async setMapEvents(map: GoogleMap, events: MapEvents) {
    if (events.onMapClick) {
      await map.setOnMapClickListener(events.onMapClick);
    }
    if (events.onBoundsChanged) {
      await map.setOnBoundsChangedListener(events.onBoundsChanged);
    }
    if (events.onCameraMoveStarted) {
      await map.setOnCameraMoveStartedListener(events.onCameraMoveStarted);
    }
    if (events.onCameraIdle) {
      await map.setOnCameraIdleListener(events.onCameraIdle);
    }
    if (events.onClusterClick) {
      await map.setOnClusterClickListener(events.onClusterClick);
    }
    if (events.onMarkerClick) {
      await map.setOnMarkerClickListener(events.onMarkerClick);
    }
  }

  public fitToBounds = (map: GoogleMap, bounds: LatLngBounds) => {
    return map.fitBounds(bounds, 100);
  };

  public getStaticMapPath(location: Location | LocationListItem) {
    const sanitizedLat = location.lat
      ? location.lat.toString().replace(".", "_")
      : "0";
    const sanitizedLng = location.lng
      ? location.lng.toString().replace(".", "_")
      : "0";
    return `${location.locationID}_${sanitizedLat}_${sanitizedLng}.png`;
  }

  public async downloadStaticMaps(locations: Locations) {
    const { mapKey } = await this.authService.getAuth();
    const staticMapPaths = Object.values(locations).map((location) => {
      return this.getStaticMapPath(location);
    });
    const fileMap = await this.fileService.checkIfFilesExist(
      staticMapPaths,
      `${Directory.Data}/STATIC_MAPS`,
    );
    const filesToDownload = Object.entries(fileMap).reduce(
      (acc: any, [filePath, exists]) => {
        if (!exists) {
          const locationID = filePath.replace("STATIC_MAPS/", "").split("_")[0];
          const location = locations[locationID];
          return [
            ...acc,
            {
              location,
              path: `STATIC_MAPS/${filePath}`,
              pathToCheck: `${locationID}*`,
            },
          ];
        }
        return acc;
      },
      [],
    );

    await this.removeOldStaticMaps(filesToDownload);
    await Promise.all(
      filesToDownload.map(
        async (file: { location: Location; path: string }) => {
          const url: any = await this.getStaticMapUrl(file.location, mapKey);
          return this.requestService.downloadFile(url, file.path);
        },
      ),
    );
  }

  public async removeOldStaticMaps(filesToDownload: any) {
    const pathsToCheck = filesToDownload.map(
      (file: { pathToCheck: string }) => {
        return file.pathToCheck;
      },
    );
    const fileMap = await this.fileService.checkIfFilesExist(
      pathsToCheck,
      `${Directory.Data}/STATIC_MAPS`,
    );
    const paths = Object.keys(fileMap).map((path) => `STATIC_MAPS/${path}`);
    return this.fileService.removeFiles(paths);
  }

  public getStaticMap(location: Location) {
    const path = this.getStaticMapPath(location);
    return this.fileService.serveFile(`STATIC_MAPS/${path}`);
  }

  public removeStaticMaps(locations: Location[] | LocationListItem[]) {
    const paths = locations.map((location) => {
      const path = this.getStaticMapPath(location);
      return location.isLocal ? "" : `STATIC_MAPS/${path}`;
    });
    return this.fileService.removeFiles(paths.filter((path) => path));
  }

  public async getStaticMapUrl(
    location: Location,
    key?: string,
    sanitize = false,
  ) {
    const apiKey = key || (await this.authService.getAuth()).mapKey;
    const address =
      location.lat && location.lng
        ? `${location.lat},${location.lng}`
        : location.output.address;
    const url = `https://maps.googleapis.com/maps/api/staticmap?key=${apiKey}&zoom=16&size=640x250&markers=color:blue|label:S|${address}`;
    return sanitize ? this.sanitizer.bypassSecurityTrustUrl(url) : url;
  }

  public async createMarkers(
    map: GoogleMap,
    locations: SearchLocation[],
  ): Promise<{
    markers: MapMarker[];
    markerMap: { [key: string]: MapMarker };
  }> {
    const offsetAmount = 0.00001; // This is approximately 1 meter.
    const seenCoordinates = new Map<string, number>();
    const res = await locations.reduce(
      async (acc: any, location: SearchLocation) => {
        const coordKey = `${location.lat}|${location.lng}`;
        const offsetCount = seenCoordinates.get(coordKey) || 0;
        seenCoordinates.set(coordKey, offsetCount + 1);

        if (!location.lat || !location.lng) {
          return acc;
        }
        const awaitedAcc = await acc;
        const lat = Number(location.lat) + offsetAmount * offsetCount;
        const lng = Number(location.lng);
        const markerId = await map.addMarker({
          title: location.output.name,
          iconUrl: location.isDownloaded
            ? "assets/marker_green.png"
            : "assets/marker_red.png",
          snippet: `<strong>${location.output.name}</strong><br />${location.output.address}<br />${location.output.info}`,
          coordinate: {
            lat,
            lng,
          },
        });
        return {
          markers: [
            ...awaitedAcc.markers,
            {
              row: location,
              id: markerId,
              lat,
              lng,
            },
          ],
          markerMap: {
            ...awaitedAcc.markerMap,
            [markerId]: {
              row: location,
              id: markerId,
              lat,
              lng,
            },
          },
        };
      },
      { markers: [], markerMap: {} },
    );
    return res;
  }

  public updateMarkers(
    markers: MapMarker[],
    locations: { [locationID: string]: SearchLocation },
  ) {
    const res = markers.reduce(
      (
        acc: { markers: MapMarker[]; markerMap: { [key: string]: MapMarker } },
        marker: MapMarker,
      ) => {
        return {
          markers: [
            ...acc.markers,
            {
              row: locations[marker.row.locationID] || marker.row,
              id: marker.id,
              lat: marker.lat,
              lng: marker.lng,
            },
          ],
          markerMap: {
            ...acc.markerMap,
            [marker.id]: {
              row: locations[marker.row.locationID] || marker.row,
              id: marker.id,
              lat: marker.lat,
              lng: marker.lng,
            },
          },
        };
      },
      { markers: [], markerMap: {} },
    );
    return res;
  }

  public getCenter(markers: MapMarker[]) {
    const bounds = this.getBounds(markers);
    return bounds.center;
  }

  public calculateCenter(
    sw: { lat: number; lng: number },
    ne: { lat: number; lng: number },
  ): { lat: number; lng: number } {
    const lat = (sw.lat + ne.lat) / 2;

    let lng;
    if (Math.abs(ne.lng - sw.lng) > 180) {
      // Adjust the longitudes for anti-meridian crossing.
      const adjustedSwLng = sw.lng < 0 ? sw.lng + 360 : sw.lng;
      const adjustedNeLng = ne.lng < 0 ? ne.lng + 360 : ne.lng;
      lng = (adjustedSwLng + adjustedNeLng) / 2;

      // Ensure the longitude is within the [-180, 180] range.
      if (lng > 180) {
        lng -= 360;
      }
    } else {
      lng = (sw.lng + ne.lng) / 2;
    }

    return { lat, lng };
  }

  public getBounds(markers: MapMarker[]) {
    const sw = {
      lat: markers[0]?.lat !== undefined ? markers[0]?.lat : 48.1667,
      lng: markers[0]?.lng !== undefined ? markers[0]?.lng : -100.1667,
    };
    const ne = { ...sw };

    // Find the actual southwest and northeast corners
    markers.forEach((marker) => {
      if (marker.lat < sw.lat) {
        sw.lat = marker.lat;
      }
      if (marker.lng < sw.lng) {
        sw.lng = marker.lng;
      }
      if (marker.lat > ne.lat) {
        ne.lat = marker.lat;
      }
      if (marker.lng > ne.lng) {
        ne.lng = marker.lng;
      }
    });

    const center = this.calculateCenter(sw, ne);
    return new LatLngBounds({
      southwest: sw,
      northeast: ne,
      center,
    });
  }

  public async clearMarkers(
    map: GoogleMap,
    markers: MapMarker[],
  ): Promise<void> {
    const markerIds = markers.map((marker) => marker.id);
    if (markerIds.length) {
      await map.removeMarkers(markerIds);
    }
  }

  public async resetMarkers(
    map: GoogleMap,
    markers: MapMarker[],
    allMarkers: MapMarker[],
  ) {
    await this.clearMarkers(map, markers);
    return this.createMarkers(
      map,
      allMarkers.map((marker: MapMarker) => marker.row),
    );
  }

  public createMarkerMap(markers: MapMarker[]) {
    return markers.reduce(
      (acc: { [key: string]: MapMarker }, marker: MapMarker) => {
        return {
          ...acc,
          [marker.id]: marker,
        };
      },
      {},
    );
  }

  public async filterMarkers(
    map: GoogleMap,
    markers: MapMarker[],
    allMarkers: MapMarker[],
    filter: any,
    resetMarkers: boolean = false,
  ): Promise<{
    markers: MapMarker[];
    markerMap: { [key: string]: MapMarker };
  }> {
    let newMarkers = markers;
    if (resetMarkers) {
      const res = await this.resetMarkers(map, markers, allMarkers);
      newMarkers = res.markers;
    }
    const [filteredMarkers, markersToRemove] = partition(newMarkers, filter);
    const filteredMarkerMap = this.createMarkerMap(filteredMarkers);
    await this.clearMarkers(map, markersToRemove);
    await map.disableClustering();
    await map.enableClustering(2);
    return { markers: filteredMarkers, markerMap: filteredMarkerMap };
  }
}
